import { hasGlobalScope, scopedProjectIds } from '@obraplan/core/domain/access/permission-resolver';
import type { PermissionDefinition } from '@obraplan/core/domain/access/permissions';
import type { Role } from '@obraplan/core/domain/access/role';
import { ROLE_CATALOG, type RoleKey } from '@obraplan/core/domain/access/roles';
import { compareNames } from '@obraplan/core/domain/shared/naming';
import type { AuditLog } from '@obraplan/core/domain/audit/audit-log';
import type { Project } from '@obraplan/core/domain/structure/project';
import type { WorkCalendar } from '@obraplan/core/domain/calendar/work-calendar';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import type { AppError } from '../../errors/app-error';
import type { StorageEstimate, StorageInspector } from '../../ports/storage-inspector';
import type {
  AuditLogRepository,
  PermissionRepository,
  ProjectRepository,
  RoleRepository,
  WorkCalendarRepository,
} from '../../ports/repositories';
import type { PermissionChecker } from '../../services/permission-checker';
import type { Session } from '../../session';

/**
 * Obras que o usuário alcança.
 *
 * Papel global vê todas; papel por obra vê apenas as suas (RN-132). Nenhuma consulta do
 * sistema devolve obras sem passar por aqui.
 */
export class ListAccessibleProjects {
  constructor(private readonly projects: ProjectRepository) {}

  async execute(session: Session): Promise<Project[]> {
    if (hasGlobalScope(session.assignments)) {
      return this.projects.listAll();
    }
    const ids = [...scopedProjectIds(session.assignments)];
    return this.projects.listByIds(ids);
  }
}

/**
 * Os papéis, na ordem em que se lê: os de fábrica na ordem do catálogo — do que pode mais
 * ao que só olha —, e os criados na tela depois, pelo nome.
 */
export class ListRoles {
  constructor(private readonly roles: RoleRepository) {}

  async execute(): Promise<Role[]> {
    const roles = await this.roles.listAll();
    const factoryOrder = new Map(ROLE_CATALOG.map((definition, index) => [definition.key, index]));
    const rank = (role: Role) => factoryOrder.get(role.key as RoleKey) ?? factoryOrder.size;
    return roles.sort((a, b) => rank(a) - rank(b) || compareNames(a.name, b.name));
  }
}

export class ListPermissions {
  constructor(private readonly permissions: PermissionRepository) {}

  execute(): Promise<PermissionDefinition[]> {
    return this.permissions.listAll();
  }
}

export class GetDefaultWorkCalendar {
  constructor(private readonly calendars: WorkCalendarRepository) {}

  execute(): Promise<WorkCalendar | undefined> {
    return this.calendars.findDefault();
  }
}

/** Consumo do armazenamento e se o navegador prometeu não expurgar os dados. */
export class GetStorageStatus {
  constructor(private readonly inspector: StorageInspector) {}

  execute(): Promise<StorageEstimate> {
    return this.inspector.estimate();
  }
}

export class RequestStoragePersistence {
  constructor(private readonly inspector: StorageInspector) {}

  execute(): Promise<boolean> {
    return this.inspector.requestPersistence();
  }
}

/** Trilha de auditoria recente. Exige permissão — é informação sobre todos os operadores. */
export class ListRecentAudit {
  constructor(
    private readonly logs: AuditLogRepository,
    private readonly permissions: PermissionChecker,
  ) {}

  async execute(session: Session, limit = 100): Promise<Result<AuditLog[], AppError>> {
    const allowed = this.permissions.ensure(session, PERMISSION.AUDIT_VIEW);
    if (!allowed.ok) return err(allowed.error);
    return ok(await this.logs.listRecent(limit));
  }
}
